import { useState } from "react";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const contactInfo = [
  { icon: MapPin, label: "Address", value: "10523 Burbank Blvd unit 111, North Hollywood, CA 91601" },
  { icon: Phone, label: "Phone", value: "(818) 927-2222" },
  { icon: Mail, label: "Email", value: "sales@jayz.one" },
  { icon: Clock, label: "Hours", value: "Monday – Friday, 10:00 AM – 4:00 PM PST" },
];

const Contact = () => {
  const headingRef = useScrollReveal();
  const infoRef = useScrollReveal(100);
  const formRef = useScrollReveal(250);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const id = crypto.randomUUID();
      // Send confirmation to the person who filled out the form
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-confirmation",
          recipientEmail: form.email,
          idempotencyKey: `contact-confirm-${id}`,
          templateData: { name: form.name },
        },
      });
      // Notify sales with full details
      await supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "contact-notification",
          recipientEmail: "sales@jayz.one",
          idempotencyKey: `contact-notify-${id}`,
          templateData: { name: form.name, email: form.email, message: form.message },
        },
      });
    } catch {
      // Still show success — the form was submitted
    }
    setSending(false);
    setSubmitted(true);
  };

  return (
    <section id="contact" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1320px] px-6">
        <div ref={headingRef} className="mx-auto max-w-2xl text-center" data-reveal>
          <h2
            className="text-3xl font-bold tracking-tight text-foreground md:text-4xl"
            style={{ textWrap: "balance" }}
          >
            Get in Touch
          </h2>
          <p className="mt-4 text-muted-foreground" style={{ textWrap: "pretty" }}>
            Ready to streamline your technology? Reach out and we'll schedule a free consultation.
          </p>
        </div>

        <div className="mt-14 grid gap-12 lg:grid-cols-2">
          {/* Info */}
          <div ref={infoRef} className="space-y-6" data-reveal="left">
            {contactInfo.map((c) => (
              <div key={c.label} className="flex gap-4">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
                  <c.icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {c.label}
                  </p>
                  <p className="mt-0.5 text-sm text-foreground">{c.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Form */}
          <div ref={formRef} data-reveal="right">
            {submitted ? (
              <div className="flex h-full flex-col items-center justify-center rounded-card border border-border bg-card p-10 text-center shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-50 text-green-600">
                  <Mail className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-foreground">Thank you for reaching out</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  One of our team members will get back to you shortly.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="flex flex-col gap-5 rounded-card border border-border bg-card p-8 shadow-sm"
              >
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground">
                    Name
                  </label>
                  <input
                    id="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground">
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="you@company.com"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground">
                    Message
                  </label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    placeholder="Tell us about your project or challenge..."
                  />
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  className="inline-flex h-12 items-center justify-center rounded-btn bg-primary px-6 text-sm font-semibold text-primary-foreground shadow-sm transition-colors duration-150 hover:bg-primary/85 active:scale-[0.97] disabled:opacity-60"
                >
                  {sending ? "Sending…" : "Request Consultation"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
